import { RecurringTypesEnum } from "dash-common";
import { SubscriptionProduct } from "gql/generated.graphql";
import { TFunction } from "i18next";
import {
  getDisplayName,
  getDisplayPricePerMonth,
} from "../SubscriptionPlanCardV3.helpers";
import {
  SUBSCRIPTION_COLORS,
  SUBSCRIPTION_CONFIG,
} from "../../SubscriptionComponents.constants";

interface ICardHeaderProps {
  product: SubscriptionProduct;
  colors: {
    bgColor: string;
    titleColor: string;
    textColor: string;
    priceColor: string;
    buttonBg: string;
    buttonBgHover: string;
    buttonText: string;
    bookDemoColor: string;
  };
  planKey: string;
  isPro: boolean;
  isDiscontinued: boolean;
  t: TFunction<["pages", "common"], undefined>;
}

export default function CardHeader({
  product,
  colors,
  planKey,
  isPro,
  isDiscontinued,
  t,
}: ICardHeaderProps) {
  // Get billing period label based on recurring type
  const getBillingLabel = () => {
    if (product.recurringType === RecurringTypesEnum.YEAR) {
      return t(SUBSCRIPTION_CONFIG.yearly.billingPeriodKey);
    } else if (product.recurringType === RecurringTypesEnum.SIXMONTH) {
      return t(SUBSCRIPTION_CONFIG.sixMonth.billingPeriodKey);
    }
    return null;
  };

  const billingLabel = getBillingLabel();

  return (
    <div className="p-5 pb-0" style={{ backgroundColor: colors.bgColor }}>
      {/* Title with Popular/Discontinued Badge */}
      <div className="flex items-center gap-2 mb-6">
        <h3
          className="text-2xl"
          style={{ color: colors.titleColor, fontFamily: "Work Sans Bold" }}
        >
          {getDisplayName(product.name)}
        </h3>
        {isPro && !isDiscontinued && (
          <span
            className="text-xs font-semibold px-5 py-1 rounded-full ml-4"
            style={{
              backgroundColor: colors.buttonBg,
              color: SUBSCRIPTION_COLORS.primary,
            }}
          >
            {t("common:labels.popular")}
          </span>
        )}
        {isDiscontinued && (
          <span className="text-xs font-semibold px-3 py-1 rounded-full ml-2 bg-gray-400 text-white">
            {t("pages:subscriptionPlans.discontinued")}
          </span>
        )}
        {product.legacy && !isDiscontinued && (
          <span
            className="text-xs font-semibold px-5 py-1 rounded-full ml-4"
            style={{
              backgroundColor: colors.buttonBg,
              color: SUBSCRIPTION_COLORS.primary,
            }}
          >
            {t("pages:subscriptionPlans.legacyPrice")}
          </span>
        )}
      </div>

      {/* Description */}
      <p
        className="text-sm mb-6 min-h-[40px]"
        style={{ color: colors.textColor, opacity: 0.9 }}
      >
        {t(`common:subscriptionProductsCardsDescriptions.${planKey}`)}
      </p>

      {/* Price */}
      <div className="mb-1">
        <span
          className="font-bold"
          style={{ color: colors.priceColor, fontSize: "40px" }}
        >
          ${getDisplayPricePerMonth(product).toFixed(2)}
        </span>
        <span
          className="ml-2"
          style={{ color: colors.textColor, fontSize: "12px" }}
        >
          {t("pages:subscriptionPlans.perMonth")}
        </span>
      </div>

      {/* Billing Period Label */}
      {billingLabel ? (
        <p
          className="text-xs mb-6 italic"
          style={{ color: colors.textColor, opacity: 0.8 }}
        >
          {billingLabel}
        </p>
      ) : (
        <div className="text-xs mb-6" style={{ height: "16px" }}></div>
      )}
    </div>
  );
}
